// Small DOM helpers shared by every client UI module.

/** Typed `document.getElementById`. */
export const $ = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

export type Screen = "menu" | "lobby" | "game";
const SCREENS: Screen[] = ["menu", "lobby", "game"];

/** Show one top-level screen (menu / lobby / game), hiding the others. */
export function show(name: Screen): void {
  for (const id of SCREENS) {
    const el = $(id);
    const wasHidden = el.classList.contains("hidden");
    el.classList.toggle("hidden", id !== name);
    // Replay the enter transition each time a screen becomes visible.
    if (id === name && wasHidden) replay(el, "screen-enter");
  }
}

/** Restart a one-shot CSS animation by re-adding its class. */
export function replay(el: HTMLElement, cls: string): void {
  el.classList.remove(cls);
  void el.offsetWidth; // force reflow so the animation restarts
  el.classList.add(cls);
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
/** Briefly flash a message at the bottom of the screen. */
export function toast(message: string): void {
  const el = $("toast");
  el.textContent = message;
  el.classList.remove("hidden");
  replay(el, "toast-in");
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.add("hidden"), 3000);
}

/** Escape a string for safe interpolation into innerHTML. */
export function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!
  );
}
